import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import CharacterDetail from "@/components/app/CharacterDetail";
import PageHeader from "@/components/app/PageHeader";
import { useLang } from "@/context/LanguageContext";

const cellTint = {
  base: "bg-white/80",
  dakuten: "bg-yuzu/40",
  handakuten: "bg-peach/40",
  combo: "bg-lavender/40",
};
const kanjiTints = ["bg-white/80", "bg-matcha/40", "bg-mint/50", "bg-sky/40", "bg-ramune/40"];

const KANA_TABS = [
  { id: "base", label_en: "Base (46)", label_pt: "Base (46)" },
  { id: "dakuten", label_en: "Dakuten ゛ (20)", label_pt: "Dakuten ゛ (20)" },
  { id: "handakuten", label_en: "Handakuten ゜ (5)", label_pt: "Handakuten ゜ (5)" },
  { id: "combo", label_en: "Yōon きゃ (33)", label_pt: "Yōon きゃ (33)" },
  { id: "all", label_en: "All", label_pt: "Todos" },
];

export default function KanaGrid({ system, title, subtitle, level, chartLabel }) {
  const { kanjiMeaning, lang, t } = useLang();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [kanaType, setKanaType] = useState("base");

  useEffect(() => {
    setLoading(true);
    const url = system === "kanji" ? `/characters/kanji${level ? `?level=${level}` : ""}` : `/characters/${system}`;
    api.get(url).then(({ data }) => { setItems(data); setLoading(false); });
  }, [system, level]);

  const filtered = useMemo(() => {
    if (system === "kanji" || kanaType === "all") return items;
    return items.filter((it) => (it.type || "base") === kanaType);
  }, [items, kanaType, system]);

  useEffect(() => {
    if (filtered.length && (!selected || !filtered.find((x) => x.char === selected.char))) setSelected(filtered[0]);
  }, [filtered, selected]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <PageHeader eyebrow={chartLabel || `${system} chart`} title={title} subtitle={subtitle} />

      {loading ? (
        <div className="animate-pulse text-ink-soft">{t("loading")}</div>
      ) : (
        <div className="grid lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            {system !== "kanji" && (
              <div className="kawaii-card inline-flex flex-wrap gap-1 p-1.5 mb-6" data-testid="kana-type-tabs">
                {KANA_TABS.map((tab) => (
                  <button key={tab.id} data-testid={`kana-tab-${tab.id}`} onClick={() => setKanaType(tab.id)}
                    className={`kawaii-pill-nav !text-xs !py-2 ${kanaType === tab.id ? "active" : ""}`}>
                    {lang === "pt" ? tab.label_pt : tab.label_en}
                  </button>
                ))}
              </div>
            )}

            <div className="grid grid-cols-5 sm:grid-cols-7 md:grid-cols-8 gap-2 sm:gap-2.5">
              {filtered.map((it, idx) => {
                const isActive = selected && selected.char === it.char;
                const label = system === "kanji" ? kanjiMeaning(it) : it.romaji;
                const tint = system === "kanji" ? kanjiTints[idx % kanjiTints.length] : cellTint[it.type || "base"];
                return (
                  <button key={it.char} data-testid={`${system}-cell-${it.romaji || it.meaning}`}
                    onClick={() => setSelected(it)} title={label}
                    className={`kawaii-card !rounded-2xl aspect-square flex flex-col items-center justify-center transition-transform duration-200 hover:scale-105 hover:z-10 ${tint} ${isActive ? "!bg-ink !text-white shadow-soft-lg scale-105 z-10" : ""}`}>
                    <span className="font-jp text-2xl sm:text-3xl leading-none">{it.char}</span>
                    <span className={`text-[10px] sm:text-[11px] font-extrabold mt-1 uppercase truncate max-w-full px-1 ${isActive ? "text-white/70" : "text-ink-soft"}`}>{label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-5 lg:sticky lg:top-24">
            {selected && (
              <div className="kawaii-pop" key={selected.char}>
                <CharacterDetail item={selected} system={system} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
